import {ESCAPE, hasModifierKey} from '@angular/cdk/keycodes';
import {AsyncPipe, NgIf} from '@angular/common';
import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FlexLayoutModule} from '@angular/flex-layout';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatOptionModule} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {
  convertToDateObject,
  endDateMaxValue,
  endDateMinValue,
  startDateMaxValue,
  startDateMinValue
} from '@features/leasing/leasing.periods.util';
import {AppSharedModule} from '@modules/app-shared.module';
import {PanelType} from '@shared-components/info-panel/info-panel.component';
import {DateUtilsService} from '@shared-services/dateUtils.service';
import {PaginatorService} from '@shared-services/paginator.service';
import {UtilsService} from '@shared-services/utils.service';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {toIsoDateEndOfDay} from "@ui-kit/framework/util/static/date-utils";
import {LeasePricingPeriod, OccupancyType, PaymentMethod, RentalPricingOption} from '../../../leasing.model';

@Component({
  selector: 'app-pricing-period-dialog',
  templateUrl: './pricing-period-dialog.component.html',
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    AppSharedModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatTableModule,
    MatPaginatorModule,
    NgIf,
    AsyncPipe,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
  ],
  standalone: true
})
export class PricingPeriodDialogComponent implements OnInit {

  @ViewChild('paymentMethodsPaginator') set paymentMethodsPaginator(paginator: MatPaginator) {
    if (paginator) paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    if (this.paymentMethodsDataSource) this.paymentMethodsDataSource.paginator = paginator;
  }

  loading$ = this.loader.loading$;
  isEditing: boolean = false;
  allPeriods: LeasePricingPeriod[] = [];
  selectedPeriod: Partial<LeasePricingPeriod>;
  leaseId: string;
  paymentMethods: PaymentMethod[] = [];
  paymentMethodsDataSource: MatTableDataSource<PaymentMethod> = new MatTableDataSource<PaymentMethod>([]);
  displayedColumns = ['collectionType', 'transactionFeeType', 'transactionFee'];

  fixedRateTypes = [
    {label: 'Fixed Rental per m² per month', code: RentalPricingOption.FIXED_PER_SQM_PER_MONTH},
    {label: 'Fixed Rental per month', code: RentalPricingOption.FIXED_PER_MONTH},
    {label: 'Fixed Rental for period', code: RentalPricingOption.FIXED_FOR_PERIOD},
  ]

  constructor(public dialogRef: MatDialogRef<PricingPeriodDialogComponent>,
              public utils: UtilsService,
              public loader: LoadingService,
              public paginatorService: PaginatorService,
              public dateUtils: DateUtilsService,
              @Inject(MAT_DIALOG_DATA) public data: any) {
    this.isEditing = data.isEditing;
    this.allPeriods = data.allPeriods;
    this.allPeriods.forEach(period => {
      period.startDate = convertToDateObject(period.startDate);
      period.endDate = convertToDateObject(period.endDate);
      period.earliestTransactionDate = convertToDateObject(period.earliestTransactionDate);
      period.latestTransactionDate = convertToDateObject(period.latestTransactionDate);
    })
    if (data.isEditing) {
      this.selectedPeriod = data.selectedPeriod;
      this.selectedPeriod.startDate = convertToDateObject(this.selectedPeriod.startDate);
      this.selectedPeriod.endDate = convertToDateObject(this.selectedPeriod.endDate);
      this.selectedPeriod.earliestTransactionDate = convertToDateObject(this.selectedPeriod.earliestTransactionDate);
      this.selectedPeriod.latestTransactionDate = convertToDateObject(this.selectedPeriod.latestTransactionDate);
    } else {
      let startDate = this.allPeriods.length == 0 ? this.convertDate(data.leaseStartDate) : null;
      let endDate = this.allPeriods.length == 0 ? this.convertDate(data.leaseEndDate) : null;
      this.selectedPeriod = {
        uuid: '',
        leaseUuid: '',
        leasePaymentConfigUuid: '',
        applyTransactionFee: null,
        transactionFeeType: '',
        transactionFee: 0,
        rentalPricingOption: RentalPricingOption.FIXED_PER_SQM_PER_MONTH,
        rentalPricingTurnoverPercentageValue: 0,
        fixedRentalPricingValue: 0,
        startDate: startDate,
        endDate: endDate,
        isEditable: true,
        latestTransactionDate: null,
        earliestTransactionDate: null,
        bookingPricing: null,
      }
    }
    this.leaseId = data.leaseId;
    this.paymentMethods = data.paymentMethods;
    this.paymentMethodsDataSource.data = data.paymentMethods;

    // CS-1661: the opener reads response.save, so Escape and a backdrop click close like Cancel
    // instead of closing with no result
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => this.cancelChanges());
    this.dialogRef.keydownEvents().subscribe(event => {
      // same rule as MatDialog's own Escape handling
      if (event.keyCode === ESCAPE && !hasModifierKey(event)) {
        event.preventDefault();
        this.cancelChanges();
      }
    });
  }

  ngOnInit(): void {
  }

  close() {
    this.cancelChanges();
  }

  cancelChanges(): void {
    this.dialogRef.close({save: false, periodToSave: null})
  }

  private convertDate(date: Date | string | null): Date | null {
    if (!date) return null;
    const formattedDate = this.dateUtils.displayOffsetDateTime(date);
    return formattedDate ? new Date(formattedDate) : null;
  }

  saveChanges(): void {
    this.dialogRef.close({save: true, periodToSave: this.selectedPeriod});
  }

  applyTransactionFee(event: any) {
    this.selectedPeriod.applyTransactionFee = event.checked;
  }

  displayFeeType(type: string): string {
    if (type === 'RATE') return 'Turnover percentage';
    if (type === 'FIXED') return 'Fixed Rate';
    return '-';
  }

  get startDateMinValue(): any {
    return startDateMinValue(this.selectedPeriod, this.allPeriods, this.isEditing);
  }

  get startDateMaxValue(): any {
    return startDateMaxValue(this.selectedPeriod, this.allPeriods);
  }

  get endDateMinValue(): any {
    return endDateMinValue(this.selectedPeriod, this.allPeriods);
  }

  get endDateMaxValue(): any {
    return endDateMaxValue(this.selectedPeriod, this.allPeriods);
  }

  onStartDateChange(newDate: Date) {
    this.selectedPeriod.startDate = newDate;
  }

  onEndDateChange(newDate: Date) {
    this.selectedPeriod.endDate = toIsoDateEndOfDay(newDate);
  }
  get displayPaymentMethodsTable(): boolean {
    return this.selectedPeriod.applyTransactionFee === true;
  }

  protected readonly PanelType = PanelType;
  protected readonly OccupancyType = OccupancyType;
  protected readonly Date = Date;
  protected readonly RentalPricingOption = RentalPricingOption;
}
